import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ORDERS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const OrderSplitForm = NiceModal.create(({ selectedOrders }) => {
	const { t } = useTranslation();
	const modal = useModal();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const defaultColSpan = {
		md: 6
	};

	const { showError, showSuccess } = useAlert();

	const [isLoading, setIsLoading] = useState(false);
	const [buttonDisabled, setButtonDisabled] = useState(false);
	const [productsData, setProductsData] = useState([]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					products: yup.array().of(
						yup.object().shape({
							splitQuantity: yup.number().nullable()
						})
					)
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderNumber: selectedOrders.orderID,
			orderDate: selectedOrders.createdAt,
			grandTotal: selectedOrders.grandTotal,
			paymentMethod: selectedOrders.paymentMethod,
			totalDue: selectedOrders.totalDue,
			newOrderGrandTotal: 0,
			newOrderCODAmount: 0,
			grandLeft: selectedOrders.grandTotal,
			CODLeft: selectedOrders.totalDue
		}
	});

	const { handleSubmit, getValues, setValue, watch, reset } = formMethods;

	const paymentMethodWatcher = watch('paymentMethod');
	const orderDateWatcher = watch('orderDate');
	const currencyWatcher = watch('currency');

	useEffect(() => {
		fillForm();
	}, [selectedOrders.ID]);

	const onSubmit = async (data) => {
		let errorCount = 0;
		let splitQuantityCount = 0;
		let quantityCount = 0;

		data.products.map((item) => {
			if (item.splitQuantity > item.quantity || item.splitQuantity < 0) {
				errorCount += 1;
			}
			splitQuantityCount += item.splitQuantity ? item.splitQuantity : 0;
			quantityCount += item.quantity;
		});

		if (errorCount > 0) {
			showError({
				header: `error`,
				message: 'errorMsg.wrongQtyEntered'
			});
		} else if (splitQuantityCount === quantityCount) {
			showError({
				header: `error`,
				message: 'errorMsg.youcannotAllQtytoSplit'
			});
		} else if (splitQuantityCount === 0) {
			showError({
				header: `error`,
				message: 'errorMsg.plsSelectOneOrMoreProduct'
			});
		} else if (
			(paymentMethodWatcher.toLowerCase() === 'cod' && parseFloat(data.CODLeft) < 0) ||
			parseFloat(data.grandLeft) < 0
		) {
			showError({
				header: `error`,
				message: 'errorMsg.grandTotalOrCODAmountCannotBeNegative'
			});
		} else {
			const products = data.products.filter((item) => item.splitQuantity > 0);
			try {
				const response = await axios.post(ORDER_ACTIONS.SPLIT_ORDER, {
					otoOrderID: selectedOrders.ID,
					grandTotal: data.grandTotal,
					totalDue: data.totalDue,
					splitCODAmount: parseFloat(data.CODLeft) || undefined,
					splitGrandTotal: parseFloat(data.grandLeft) || undefined,
					paymentMethod: data.paymentMethod,
					products: products.map((item) => ({
						otoProductID: item.ID,
						splitQuantity: item.splitQuantity
					}))
				});

				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListsInPage());
					modal.remove();
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			}
		}
	};

	const fillForm = useCallback(() => {
		if (selectedOrders.ID) {
			setIsLoading(true);
			axios
				.post(ORDERS.GET_ORDER_ITEMS_BY_ID, { ID: selectedOrders.ID })
				.then((response) => {
					const { data, status } = response;
					if (data.length === 0) {
						showError({
							header: `error`,
							message: 'errorMsg.noProductFound'
						});
						setButtonDisabled(true);
					}

					if (status === 200) {
						if (data.find((a) => a.itemType === 'bundle')) {
							showError({
								header: 'error',
								message: 'errorMsg.cannotSplitOrdersWithBundleItems'
							});
							modal.remove();
							return;
						}
						reset({
							orderNumber: selectedOrders.orderID,
							orderDate: selectedOrders.createdAt,
							grandTotal: selectedOrders.grandTotal,
							paymentMethod: selectedOrders.paymentMethod,
							totalDue: selectedOrders.totalDue,
							currency: selectedOrders.currency || ' ',
							products: data,
							newOrderGrandTotal: 0,
							newOrderCODAmount: 0,
							grandLeft: selectedOrders.grandTotal,
							CODLeft: selectedOrders.totalDue
						});
						setProductsData(data);
					} else throw new Error('Server Error');
				})
				.catch((error) => {
					AppLogger.error(error);
					showError({
						header: `error`,
						message: `${error.message}`
					});
				})
				.finally(() => setIsLoading(false));
		}
	}, [selectedOrders.ID]);

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-orderSplit">
			<ModalTitle>
				<Typography variant="p1">
					{t('orderSplit')} ({watch('orderNumber')})
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Alert content="orderSplitInfo" style={{ marginBottom: '2rem' }} showIcon />

				<Form formMethods={formMethods}>
					<FormItem label="paymentMethod" colSpan={defaultColSpan} isLoading={isLoading}>
						<StringInput value={t(watch('paymentMethod'))} disabled />
					</FormItem>
					<FormItem label="orderDate" colSpan={defaultColSpan} isLoading={isLoading}>
						<DateInput
							name="orderDate"
							disabled
							value={orderDateWatcher}
							setValue={(value) => {
								setValue('orderDate', value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>

					{paymentMethodWatcher.toLowerCase() === 'cod' && (
						<FormItem label="splitCODAmount" colSpan={defaultColSpan} isLoading={isLoading}>
							<CurrencyInput
								name="CODLeft"
								disabled
								value={currencyWatcher}
								setValue={(value) => {
									setValue(`currency`, value ? Number(value.toFixed(2)) : value, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
							/>
						</FormItem>
					)}

					<FormItem label="grandTotal" colSpan={defaultColSpan} isLoading={isLoading}>
						<CurrencyInput
							name="grandLeft"
							value={currencyWatcher}
							disabled
							setValue={(value) => {
								setValue(`currency`, value ? Number(value.toFixed(2)) : value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>

					{paymentMethodWatcher.toLowerCase() === 'cod' && (
						<FormItem label="newOrderCODAmount" colSpan={defaultColSpan} isLoading={isLoading}>
							<IntegerInput
								disabled={watch('newOrderCODAmount') === 0}
								value={watch('newOrderCODAmount')}
								setValue={(value) => {
									if (value > watch('totalDue')) return;
									if (value > watch('newOrderGrandTotal')) return;
									setValue(`newOrderCODAmount`, value ? Number(value.toFixed(2)) : value, {
										shouldValidate: true,
										shouldDirty: true
									});
									setValue(
										'CODLeft',
										getValues('totalDue') - value
											? Number((getValues('totalDue') - value).toFixed(2))
											: getValues('totalDue') - value,
										{
											shouldValidate: true,
											shouldDirty: true
										}
									);
								}}
								max={watch('totalDue')}
								allowFloat={true}
							/>
						</FormItem>
					)}
					<FormItem label="newOrderGrandTotal" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput
							disabled={watch('newOrderGrandTotal') === 0}
							max={watch('grandTotal')}
							value={watch('newOrderGrandTotal')}
							setValue={(value) => {
								if (value > watch('grandTotal')) return;
								setValue(`newOrderGrandTotal`, value ? Number(value.toFixed(2)) : value, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(
									'grandLeft',
									getValues('grandTotal') - value
										? Number((getValues('grandTotal') - value).toFixed(2))
										: getValues('grandTotal') - value,
									{
										shouldValidate: true,
										shouldDirty: true
									}
								);
								if (value < watch('newOrderCODAmount')) {
									setValue('newOrderCODAmount', value ? Number(value.toFixed(2)) : value, {
										shouldValidate: true,
										shouldDirty: true
									});
									setValue(
										'CODLeft',
										getValues('totalDue') - value
											? Number((getValues('totalDue') - value).toFixed(2))
											: getValues('totalDue') - value,
										{
											shouldValidate: true,
											shouldDirty: true
										}
									);
								}
							}}
							allowFloat={true}
						/>
					</FormItem>

					<FormItem isLoading={isLoading}>
						<OrderDetails isLoading={isLoading} data={productsData} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					label="save"
					loading={isLoading}
					disabled={buttonDisabled}
					disabledTooltip={buttonDisabled && 'orderSplitError.noProductFound'}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OrderSplitForm;

const OrderDetails = ({ isLoading, data }) => {
	const {
		watch,
		setValue,
		getValues,
		formState: { errors }
	} = useFormContext();

	const columns = useMemo(
		() => [
			{
				header: 'product',
				size: 150,
				maxSize: 90,
				renderCell: ({ row }) => (
					<ProductImage
						data={{
							name: row.original.name,
							imageURL: row.original.image
						}}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'sku',
				accessorKey: 'SKU',
				maxSize: 70
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 40,
				maxSize: 40,
				renderCell: ({ row }) => <Typography>{row.original.quantity}</Typography>,
				showOnMobile: true
			},
			{
				header: 'newOrder',
				accessorKey: 'splitQuantity',
				size: 40,
				maxSize: 40,
				renderCell: ({ row }) => (
					<IntegerInput
						max={row.original.quantity}
						errorMessage={errors.products && errors.products[row.index]?.splitQuantity?.message}
						value={watch(`products[${row.index}].splitQuantity`)}
						setValue={(newValue) => {
							if (newValue > row.original.quantity) return;
							setValue(`products[${row.index}].splitQuantity`, newValue, {
								shouldValidate: true,
								shouldDirty: true
							});
							const allProducts = getValues(`products`);
							const total = allProducts.reduce(
								(acc, item) => acc + (item.splitQuantity || 0) * ((item.price ?? 0) + (item.tax ?? 0)),
								0
							);
							setValue(`newOrderGrandTotal`, total ? Number(total.toFixed(2)) : total, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`newOrderCODAmount`, total ? Number(total.toFixed(2)) : total, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(
								'CODLeft',
								getValues('totalDue') - total
									? Number((getValues('totalDue') - total).toFixed(2))
									: getValues('totalDue') - total,
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
							setValue(
								'grandLeft',
								getValues('grandTotal') - total
									? Number((getValues('grandTotal') - total).toFixed(2))
									: getValues('grandTotal') - total,
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
						}}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'priceWithTax',
				accessorKey: 'price',
				size: 50,
				renderCell: ({ row }) => (
					<Typography>
						{(row.original.price ?? 0) + (row.original.tax ?? 0)} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'total',
				accessorKey: 'total',
				size: 50,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.total} {row.original.currency}
					</Typography>
				)
			}
		],
		[]
	);

	return <Table columns={columns} data={data} isLoading={isLoading} />;
};
